import { CALL_AUDIO_CONSTRAINTS } from './callingService';

/**
 * Keeping a live call on a working audio device.
 *
 * A headset that leaves the OS device list — unplugged, sensor auto-off,
 * Bluetooth dropped, laptop slept — ends the `MediaStreamTrack` the call is
 * sending, and nothing re-acquires it: the call stays up on a dead device until
 * the tab is reloaded. `useAudioDeviceRecovery` watches for that and calls in
 * here to resolve where audio should be now and swap the track underneath the
 * call.
 */

/** Chrome's own id for "whatever the OS default is right now", as opposed to a
 *  pinned id that points at one physical device. Acquiring with this is what
 *  makes a call follow the system default when the CSR docks or undocks. */
export const SYSTEM_DEFAULT_DEVICE_ID = 'default';

/** A device the user picked, as we remember it. */
export interface DevicePreference {
  /** The id they chose, or null to follow the system default. */
  id: string | null;
  /** The label that id carried at the time. Ids are regenerated when a device
   *  re-enumerates, so the label is what survives a re-plug. */
  label: string | null;
}

/**
 * Where the chosen microphone and speaker are remembered, per browser.
 *
 * The choice is a property of the desk, not of the tab: a CSR picks their
 * headset once and expects to stay on it. Held only in memory, it reset to the
 * system default on every reload — which on a docked machine is the laptop
 * speaker, and is why call audio kept "defaulting to the computer speaker".
 *
 * The label is stored beside the id because the id is the weaker key: see
 * `resolveDevice`. A saved id that no longer exists costs nothing — the label
 * finds the same headset, and failing that we fall back to the system default.
 */
const DEVICE_PREFERENCE_KEY = 'notifi.voip.audioDevices';

export interface DevicePreferences {
  mic: DevicePreference;
  speaker: DevicePreference;
}

const noPreference = (): DevicePreference => ({ id: null, label: null });

const nothingRemembered = (): DevicePreferences => ({
  mic: noPreference(),
  speaker: noPreference(),
});

/** Anything at all can be sitting on the key — a half-written value, an older
 *  shape, another tool's data. Take only the two strings we understand. */
function preferenceOf(value: unknown): DevicePreference {
  if (!value || typeof value !== 'object') return noPreference();
  const { id, label } = value as { id?: unknown; label?: unknown };
  return {
    id: typeof id === 'string' && id ? id : null,
    label: typeof label === 'string' && label ? label : null,
  };
}

export function readDevicePreferences(): DevicePreferences {
  try {
    const raw = localStorage.getItem(DEVICE_PREFERENCE_KEY);
    if (!raw) return nothingRemembered();
    const parsed = JSON.parse(raw) as { mic?: unknown; speaker?: unknown };
    return { mic: preferenceOf(parsed?.mic), speaker: preferenceOf(parsed?.speaker) };
  } catch {
    // Private mode, storage blocked, or the value is not JSON. Either way the
    // softphone still has to start, on the system default.
    return nothingRemembered();
  }
}

export function writeDevicePreferences(preferences: DevicePreferences): void {
  try {
    localStorage.setItem(DEVICE_PREFERENCE_KEY, JSON.stringify(preferences));
  } catch {
    // Storage blocked — the choice still holds for the rest of this session.
  }
}

export interface ResolvedDevice {
  device: MediaDeviceInfo | null;
  /** How we got there. `default` means their device is not enumerable right
   *  now and we fell back — the preference itself must survive that, or the
   *  headset would never be picked up again when it returns. */
  matched: 'id' | 'label' | 'default' | 'none';
}

export async function enumerateAudioDevices(kind: MediaDeviceKind): Promise<MediaDeviceInfo[]> {
  try {
    const all = await navigator.mediaDevices?.enumerateDevices?.();
    return (all ?? []).filter((d) => d.kind === kind && d.deviceId);
  } catch {
    // Enumeration is unsupported or blocked — callers fall back to an
    // unconstrained request, which is the right answer anyway.
    return [];
  }
}

/**
 * Where a preference points *right now*: the pinned id, then the label it was
 * pinned under, then the system default.
 *
 * The id is the weakest link. Unplug a headset and plug it back in and Chrome
 * can hand out a different id for the same physical device, so re-acquiring
 * with `deviceId: { exact: <pinned id> }` fails outright — which is the
 * difference between audio coming back and the CSR reloading the tab.
 */
export function resolveDevice(
  devices: MediaDeviceInfo[],
  preference: DevicePreference,
): ResolvedDevice {
  if (preference.id) {
    const byId = devices.find((d) => d.deviceId === preference.id);
    if (byId) return { device: byId, matched: 'id' };
  }
  if (preference.label) {
    const byLabel = devices.find((d) => d.label && d.label === preference.label);
    if (byLabel) return { device: byLabel, matched: 'label' };
  }
  const fallback =
    devices.find((d) => d.deviceId === SYSTEM_DEFAULT_DEVICE_ID) ?? devices[0] ?? null;
  return { device: fallback, matched: fallback ? 'default' : 'none' };
}

/**
 * The concrete device Chrome's `default` entry is currently pointing at.
 *
 * Needed to answer "is this call already on the right device?" while following
 * the system default: the `default` entry's own id is the literal string
 * `default`, which a live track never reports back from `getSettings()`. The
 * two are tied together by `groupId`.
 */
export function concreteDefaultDevice(devices: MediaDeviceInfo[]): MediaDeviceInfo | null {
  const systemDefault = devices.find((d) => d.deviceId === SYSTEM_DEFAULT_DEVICE_ID);
  if (!systemDefault?.groupId) return null;
  return (
    devices.find(
      (d) => d.deviceId !== SYSTEM_DEFAULT_DEVICE_ID && d.groupId === systemDefault.groupId,
    ) ?? null
  );
}

/**
 * The physical device a resolution actually points at.
 *
 * `resolveDevice` can land on the `default` pseudo-device — either because
 * nothing is pinned, or because the pinned device is unplugged and we fell
 * back. A live track always reports the concrete device it is capturing from,
 * never the string `default`, so anything comparing a call against its target
 * has to come through here. Comparing against the pseudo-device directly marks
 * every call misrouted forever and re-acquires the microphone on every pass.
 */
export function concreteDeviceFor(
  devices: MediaDeviceInfo[],
  device: MediaDeviceInfo | null,
): MediaDeviceInfo | null {
  if (!device) return null;
  return device.deviceId === SYSTEM_DEFAULT_DEVICE_ID ? concreteDefaultDevice(devices) : device;
}

/** The device a live track is actually capturing from, when the browser says. */
export function trackDeviceId(track: MediaStreamTrack | null | undefined): string | null {
  try {
    return track?.getSettings?.().deviceId ?? null;
  } catch {
    return null;
  }
}

/**
 * Whether a mic track can still carry the call.
 *
 * `ended` is terminal — the device is gone. `muted` is the browser saying it is
 * receiving no samples, which is what a slept laptop or a device grabbed
 * exclusively by another app looks like; it can recover on its own, but a call
 * sitting on one is silent until it does, so we treat both as "swap me".
 */
export function isTrackUsable(track: MediaStreamTrack | null | undefined): boolean {
  return !!track && track.readyState === 'live' && !track.muted;
}

/** The SDK surface a device swap needs. Structural on purpose — the store owns
 *  the full `TelnyxCall` shape, this only describes the parts used here. */
export interface SwappableCall {
  localStream?: MediaStream | null;
  setAudioInDevice?: (deviceId: string, muted?: boolean) => Promise<void>;
  peer?: { instance?: RTCPeerConnection | null } | null;
  options?: { localStream?: MediaStream | null; micId?: string };
}

/**
 * Point a call that is already up at `deviceId`, without dropping it.
 *
 * Prefers the SDK's `setAudioInDevice`, which does the `getUserMedia` +
 * `RTCRtpSender.replaceTrack()` and keeps its own `localStream` in step. That
 * path re-acquires with `deviceId: { exact: … }` and reads the audio sender as
 * `({ track: { kind } })`, so it throws if the id went stale between
 * enumerating and acquiring — a live possibility while devices are churning —
 * or if any sender has no track. The fallback does the same swap by hand with a
 * constraint that cannot be over-constrained.
 *
 * `muted` is passed through deliberately: a freshly acquired track arrives
 * enabled, so a call the CSR had muted would come back live without it.
 *
 * Returns the stream now feeding the call, or null if it could not be swapped.
 */
export async function swapCallMicrophone(
  call: SwappableCall,
  { deviceId, muted }: { deviceId: string | null; muted: boolean },
): Promise<MediaStream | null> {
  if (deviceId && typeof call.setAudioInDevice === 'function') {
    try {
      await call.setAudioInDevice(deviceId, muted);
      return call.localStream ?? call.options?.localStream ?? null;
    } catch {
      // Fall through and do it by hand.
    }
  }

  const senders = call.peer?.instance?.getSenders?.() ?? [];
  // A sender whose track has been cleared is still the audio sender — that is
  // exactly the state we are recovering from — so fall back to a trackless one.
  const sender = senders.find((s) => s.track?.kind === 'audio') ?? senders.find((s) => !s.track);
  if (!sender || !navigator.mediaDevices?.getUserMedia) return null;

  let stream: MediaStream;
  try {
    stream = await acquireMicrophone(deviceId);
  } catch {
    return null;
  }

  const track = stream.getAudioTracks()[0];
  if (!track) {
    stopTracks(stream);
    return null;
  }
  track.enabled = !muted;
  try {
    await sender.replaceTrack(track);
  } catch {
    stopTracks(stream);
    return null;
  }
  // Keep the SDK's own reference in step — mute/unmute and teardown read it.
  // `call.localStream` needs no separate assignment and must not get one: it is
  // a getter over `options.localStream` with no setter, so writing to it throws.
  if (call.options) call.options.localStream = stream;
  if (call.options && deviceId) call.options.micId = deviceId;
  return stream;
}

/**
 * Open `deviceId`, degrading to whatever the browser picks only when that exact
 * device can't be had.
 *
 * `exact` first, because Chrome treats an `ideal` (or bare) deviceId as a hint
 * it is free to ignore: with the headset available it still hands back the
 * system default, so a mic the CSR picked by hand never took effect. `ideal` is
 * kept as the second try for the case this fallback exists for — an id that has
 * just gone stale, where `exact` throws.
 *
 * `exactOnly` turns that second try off, for callers who have a BETTER fallback
 * than "whatever the browser picks" and would rather fail than pre-empt it.
 * Warming the microphone is one: the id going stale is exactly when the SDK's
 * own resolution — which re-finds the device by the label it was pinned under —
 * needs to run, and a warm stream from the default device would be handed over
 * in its place and silence the headset. A recovery swap on a call that is
 * already up is the opposite case: some microphone beats none, so it keeps the
 * fallback.
 */
export async function acquireMicrophone(
  deviceId: string | null,
  { exactOnly = false }: { exactOnly?: boolean } = {},
): Promise<MediaStream> {
  if (!deviceId) return navigator.mediaDevices.getUserMedia({ audio: CALL_AUDIO_CONSTRAINTS });
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: { ...CALL_AUDIO_CONSTRAINTS, deviceId: { exact: deviceId } },
    });
  } catch (error) {
    if (exactOnly) throw error;
    return navigator.mediaDevices.getUserMedia({
      audio: { ...CALL_AUDIO_CONSTRAINTS, deviceId: { ideal: deviceId } },
    });
  }
}

function stopTracks(stream: MediaStream): void {
  try {
    stream.getTracks().forEach((t) => t.stop());
  } catch {
    // Already stopped — nothing to release.
  }
}
